import React, { useContext } from 'react';

import styled from 'styled-components';

import { ListContext } from '@contexts/ListContext';

import EmptyState from '@components/Gui/EmptyState';
import NwIcon from '@components/Gui/NwIcon';

import {
    ARTICLE_BLOCK_TYPE,
    getArticleRowLayoutCssGridTemplate,
    normalizeArticleRowLayoutKey,
} from '@containers/Art/Articles/Articles.constants';
import ArtDocumentMediaPreview from '@containers/Art/ArtDocumentMediaPreview';
import { getArtMediaDocumentId, getArtStoredMediaUrl } from '@containers/Art/ArtDocuments';

import { sortByPosition } from './articleEditorDrawerHelpers';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const formatPublishedDateLabel = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(parsed);
};

/** Read-only preview: descriptor title above a paper-like card; blocks only inside the card. */
export const ArticlePreviewWrap = styled.div`
    .article-preview-shell {
        color: ${(props) => props.theme.TextColor};
        margin: 0 auto;
        max-width: 1200px;
        padding: 1.25rem clamp(1rem, 3vw, 2rem) 2rem;
    }

    /* Article Title field — metadata only, not part of rendered article body */
    .article-preview-descriptor {
        color: ${(props) => props.theme.TextColorMuted};
        font-size: ${(props) => props.theme.FontSizeSm};
        font-weight: 400;
        letter-spacing: 0.06em;
        line-height: 1.35;
        margin: 0 0 0.85rem 0;
        text-transform: uppercase;
    }

    .article-preview-meta {
        color: ${(props) => props.theme.TextColorMuted};
        font-size: ${(props) => props.theme.FontSizeSm};
        font-weight: 400;
        line-height: 1.45;
        margin: 0 0 0.85rem 0;
    }

    .article-preview-descriptor + .article-preview-meta {
        margin-top: -0.35rem;
    }

    .article-preview-card {
        background: #fff;
        box-shadow:
            0 1px 2px rgba(0, 0, 0, 0.05),
            0 4px 14px rgba(0, 0, 0, 0.07),
            0 12px 28px rgba(0, 0, 0, 0.06);
        color: ${(props) => props.theme.TextColor};
        overflow: hidden;
    }

    .article-preview-card-body {
        padding: 2.25rem clamp(1.25rem, 3vw, 2.75rem) 2.75rem;
    }

    .article-preview-rows {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 2.5rem;
    }

    .article-preview-row {
        display: grid;
        grid-template-columns: var(--article-preview-grid-template, minmax(0, 1fr));
        gap: clamp(1.25rem, 3vw, 2.25rem);
        align-items: start;
    }

    .article-preview-column {
        min-width: 0;
    }

    .article-preview-block + .article-preview-block {
        margin-top: 1.4rem;
    }

    .article-preview-block-title {
        font-size: 2rem;
        font-weight: 700;
        line-height: 1.2;
        margin: 0;
    }

    .article-preview-title-block {
        color: ${(props) => props.theme.TextColor};

        h1,
        h2,
        p {
            font-weight: 700;
            margin: 0;
        }

        h1 {
            font-size: 2rem;
            line-height: 1.2;
        }

        h2 {
            font-size: 1.5rem;
            line-height: 1.25;
        }

        .ql-align-center {
            text-align: center;
        }

        .ql-align-justify {
            text-align: justify;
        }

        .ql-align-right {
            text-align: right;
        }
    }

    .article-preview-text {
        color: ${(props) => props.theme.TextColor};
        font-size: 1rem;
        line-height: 1.7;

        p,
        ul,
        ol {
            margin: 0 0 1rem 0;
        }

        ul,
        ol {
            padding-left: 1.25rem;
        }

        h1 {
            font-size: 2rem;
            line-height: 1.2;
            margin: 0 0 1rem 0;
        }

        h2 {
            font-size: 1.5rem;
            line-height: 1.25;
            margin: 0 0 1rem 0;
        }

        a {
            color: ${(props) => props.theme.PrimaryColor};
        }

        .ql-align-center {
            text-align: center;
        }

        .ql-align-justify {
            text-align: justify;
        }

        .ql-align-right {
            text-align: right;
        }
    }

    .article-preview-file {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: ${(props) => props.theme.PrimaryColor};
        font-weight: 500;
        text-decoration: underline;
    }

    .article-preview-media {
        background: #f5f5f5;
        border: ${(props) => props.theme.DefaultBorder};
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 9rem;
        overflow: hidden;
        width: 100%;

        img,
        video {
            display: block;
            height: auto;
            max-width: 100%;
            width: 100%;
        }

        .article-preview-video-player {
            aspect-ratio: 16 / 9;
            width: 100%;
        }

        .article-preview-video-player > div {
            width: 100% !important;
            height: 100% !important;
        }

        .article-preview-video-poster {
            position: relative;
            width: 100%;
        }

        .article-preview-video-icon {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            font-size: 1.8rem;
            text-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
        }

        > svg {
            opacity: 0.45;
        }
    }

    @media (max-width: 760px) {
        .article-preview-shell {
            padding: 1rem 1rem 1.5rem;
        }

        .article-preview-card-body {
            padding: 1.75rem 1.25rem 2rem;
        }

        .article-preview-row {
            grid-template-columns: minmax(0, 1fr);
        }
    }
`;

const ArticlePreviewMedia = ({ media = {} }) => {
    return (
        <ArtDocumentMediaPreview
            documentId={getArtMediaDocumentId(media)}
            fallbackIcon={byPrefixAndName.fasl['image']}
            fallbackLabel="Article media"
            media={media}
            showControls
            videoClassName="article-preview-video-player"
            videoPosterClassName="article-preview-video-poster"
        />
    );
};

const hasHtmlTag = (value = '') => /<\/?[a-z][\s\S]*>/i.test(String(value));

const ArticlePreviewBlock = ({ block }) => {
    if (block.Type === ARTICLE_BLOCK_TYPE.TITLE) {
        if (!block.Title) return null;

        if (hasHtmlTag(block.Title)) {
            return <div className="article-preview-title-block" dangerouslySetInnerHTML={{ __html: block.Title }} />;
        }

        return <h1 className="article-preview-block-title">{block.Title}</h1>;
    }

    if (block.Type === ARTICLE_BLOCK_TYPE.MEDIA) {
        return (
            <div className="article-preview-media">
                <ArticlePreviewMedia media={block.Media} />
            </div>
        );
    }

    if (block.Type === ARTICLE_BLOCK_TYPE.FILE) {
        const fileUrl = getArtStoredMediaUrl(block.Media);
        if (!fileUrl) return null;

        return (
            <a className="article-preview-file" href={fileUrl} rel="noopener noreferrer" target="_blank">
                <NwIcon icon={byPrefixAndName.fasl['file-pdf']} />
                {block.Media?.LinkText || 'Download file'}
            </a>
        );
    }

    if (!block.Text) return null;

    return <div className="article-preview-text" dangerouslySetInnerHTML={{ __html: block.Text }} />;
};

/** @param {{ articleSnapshot: Record<string, unknown> | null }} props */
const ArticlePreviewPane = ({ articleSnapshot }) => {
    const { GetUserName } = useContext(ListContext);
    const rows = sortByPosition(articleSnapshot ? articleSnapshot.Rows : []);
    const descriptorTitle = String(articleSnapshot?.Title ?? '').trim();
    const authorName = String(articleSnapshot?.AuthorName || articleSnapshot?.authorName || '').trim();
    const authorId = articleSnapshot?.AuthorUserID ?? articleSnapshot?.authorUserID;
    const authorLabel = authorName || (authorId ? GetUserName(authorId) : '');
    const dateLabel = formatPublishedDateLabel(articleSnapshot?.PublishedDate ?? articleSnapshot?.publishedDate);
    const metaLine = [authorLabel, dateLabel].filter(Boolean).join(' · ');

    return (
        <ArticlePreviewWrap>
            <div className="article-preview-shell">
                {descriptorTitle ? <p className="article-preview-descriptor">{descriptorTitle}</p> : null}
                {metaLine ? <p className="article-preview-meta">{metaLine}</p> : null}
                <div className="article-preview-card">
                    {rows.length ? (
                        <article className="article-preview-card-body">
                            <div className="article-preview-rows">
                                {rows.map((row) => {
                                    const layoutKey = normalizeArticleRowLayoutKey(row.Layout);
                                    return (
                                        <div
                                            className="article-preview-row"
                                            key={row.ID}
                                            style={{
                                                '--article-preview-grid-template':
                                                    getArticleRowLayoutCssGridTemplate(layoutKey),
                                            }}
                                        >
                                            {sortByPosition(row.Columns).map((column) => (
                                                <div className="article-preview-column" key={column.ID}>
                                                    {sortByPosition(column.Blocks).map((block) => (
                                                        <div className="article-preview-block" key={block.ID}>
                                                            <ArticlePreviewBlock block={block} />
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </article>
                    ) : (
                        <div className="article-preview-card-body">
                            <EmptyState message="No content" />
                        </div>
                    )}
                </div>
            </div>
        </ArticlePreviewWrap>
    );
};

export default ArticlePreviewPane;
